/* eslint-disable no-underscore-dangle */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  checkInfoAPI,
  checkuserIdAPI,
  confirmCertCodeAPI,
  sendVerificationAPI,
  signupAPI,
} from '../api/auth';

const MODULE_SIGN_UP = 'signup';
const initialState = {
  pending: false,
  checking: {
    id: false,
    mail: false,
    auth: false,
    send: false,
    complete: false,
  },
  checked: { id: null, mail: null, auth: null, send: null, complete: null },
  mode: 0,
  value: {
    lastName: '',
    firstName: '',
    userId: '',
    password: '',
    passwordConfirm: '',
    email: '',
    institution: '',
  },
};

export const signUp = createAsyncThunk(
  `${MODULE_SIGN_UP}/SIGN_UP`,
  async (_undefined, { rejectWithValue, getState }) => {
    try {
      const {
        value: {
          lastName,
          firstName,
          userId,
          password,
          email,
          institution,
          tel,
        },
      } = getState().signup;
      const { result, user } = await signupAPI({
        lastName,
        firstName,
        userId,
        password,
        email,
        institution,
        tel,
      });
      if (result) {
        return { result, userPId: user._id };
      }
      return rejectWithValue('회원가입에 실패하였습니다. 다시 시도해주세요.');
    } catch (err) {
      return rejectWithValue('회원가입에 실패하였습니다. 다시 시도해주세요.');
    }
  },
);

export const checkUserId = createAsyncThunk(
  `${MODULE_SIGN_UP}/CHECK_ID`,
  async (userId, { rejectWithValue }) => {
    try {
      const { result } = await checkuserIdAPI(userId);
      if (result) {
        return result;
      }
      return rejectWithValue('이미 사용중인 아이디 입니다.');
    } catch (err) {
      return rejectWithValue(
        '알 수 없는 오류가 발생했습니다. 관리자에게 문의하세요.',
      );
    }
  },
);

export const checkInfo = createAsyncThunk(
  `${MODULE_SIGN_UP}/CHECK_INFO`,
  async (data, { rejectWithValue }) => {
    try {
      const { result } = await checkInfoAPI(data);
      if (result) {
        return result;
      }
      return rejectWithValue('이미 등록된 인증정보 입니다.');
    } catch (err) {
      return rejectWithValue(
        '알 수 없는 오류가 발생했습니다. 관리자에게 문의하세요.',
      );
    }
  },
);

export const confirmCode = createAsyncThunk(
  `${MODULE_SIGN_UP}/CONFIRM_CODE`,
  async ({ code }, { rejectWithValue }) => {
    try {
      const { result } = await confirmCertCodeAPI({
        code,
        type: 'signup',
      });
      if (result) {
        return result;
      }
      return rejectWithValue('인증에 실패하였습니다.');
    } catch (err) {
      return rejectWithValue(
        '알 수 없는 오류가 발생했습니다. 관리자에게 문의하세요.',
      );
    }
  },
);

export const sendEmail = createAsyncThunk(
  `${MODULE_SIGN_UP}/SEND_MAIL`,
  async ({ userId, email }, { rejectWithValue }) => {
    try {
      const { result, error } = await sendVerificationAPI({
        userId,
        email,
        type: 'signup',
      });
      if (result) {
        return result;
      }

      if (error) {
        const { code, desc } = error;
        if (code === '4v102a') {
          return rejectWithValue(
            '메일 전송에 필요한 정보가 부족합니다. 다시 시도해주세요.',
          );
        }
        if (code === '4v102b') {
          return rejectWithValue(
            '유효한 메일이 아닙니다. 다른 메일로 시도해주세요.',
          );
        }
        return rejectWithValue(desc);
      }
      return rejectWithValue(
        '메일 전송 중 오류가 발생하였습니다. 다시 시도해주세요.',
      );
    } catch (err) {
      return rejectWithValue(
        '메일 전송 중 오류가 발생하였습니다. 다시 시도해주세요.',
      );
    }
  },
);

export const sendVerifyMsg = createAsyncThunk(
  `${MODULE_SIGN_UP}/SEND_MESSAGE`,
  async (data, { rejectWithValue }) => {
    try {
      const { result, error } = await sendVerificationAPI({
        ...data,
        type: 'signup',
      });
      if (result) {
        return result;
      }

      if (error) {
        const { code, desc } = error;
        if (code === '4v102a') {
          return rejectWithValue(
            '인증번호 전송에 필요한 정보가 부족합니다. 다시 시도해주세요.',
          );
        }
        if (code === '4v102b') {
          return rejectWithValue(
            '유효한 인증정보가 아닙니다. 다른 정보로 시도해주세요.',
          );
        }
        return rejectWithValue(desc);
      }
      return rejectWithValue(
        '인증정보 전송 중 오류가 발생하였습니다. 다시 시도해주세요.',
      );
    } catch (err) {
      return rejectWithValue(
        '인증정보 전송 중 오류가 발생하였습니다. 다시 시도해주세요.',
      );
    }
  },
);

const signupSlice = createSlice({
  name: MODULE_SIGN_UP,
  initialState,
  reducers: {
    uncheck: (state, action) => ({
      ...state,
      checked: { ...state.checked, [action.payload]: false },
    }),
    signUpInit: () => ({
      ...initialState,
    }),
    setSignUpMode: (state, action) => ({ ...state, mode: action.payload }),
    setValue: (state, action) => ({
      ...state,
      value: { ...state.value, [action.payload.name]: action.payload.value },
    }),
  },
  extraReducers: {
    [signUp.pending.type]: (state) => ({
      ...state,
      checking: { ...state.checking, complete: true },
      checked: { ...state.checked, complete: false },
      result: null,
      error: null,
    }),
    [signUp.fulfilled.type]: (state, action) => ({
      ...state,
      pending: false,
      checking: { ...state.checking, complete: false },
      checked: { ...state.checked, complete: true },
      result: action.payload.result,
      user: { ...state.user, userPId: action.payload.userPId },
    }),
    [signUp.rejected.type]: (state, action) => ({
      ...state,
      pending: false,
      checking: { ...state.checking, complete: false },
      checked: { ...state.checked, complete: null },
      error: { ...state.error, complete: action.payload },
    }),
    [sendVerifyMsg.pending.type]: (state) => ({
      ...state,
      checking: { ...state.checking, send: true },
      checked: { ...state.checked, send: false, auth: null },
      error: { ...state.error, send: null, auth: null },
    }),
    [sendVerifyMsg.fulfilled.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, send: false },
      checked: { ...state.checked, send: action.payload },
    }),
    [sendVerifyMsg.rejected.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, send: false },
      checked: { ...state.checked, send: null },
      error: { ...state.error, send: action.payload },
    }),
    [checkUserId.pending.type]: (state) => ({
      ...state,
      checking: { ...state.checking, id: true },
      checked: { ...state.checked, id: null },
      error: { ...state.error, id: null },
    }),
    [checkUserId.fulfilled.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, id: false },
      checked: { ...state.checked, id: action.payload },
    }),
    [checkUserId.rejected.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, id: false },
      checked: { ...state.checked, id: false },
      error: { ...state.error, id: action.payload },
    }),
    [checkInfo.pending.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, [action.meta.arg.type]: true },
      checked: { ...state.checked, [action.meta.arg.type]: null },
      error: { ...state.error, [action.meta.arg.type]: null },
    }),
    [checkInfo.fulfilled.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, [action.meta.arg.type]: false },
      checked: { ...state.checked, [action.meta.arg.type]: action.payload },
    }),
    [checkInfo.rejected.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, [action.meta.arg.type]: false },
      checked: { ...state.checked, [action.meta.arg.type]: false },
      error: { ...state.error, [action.meta.arg.type]: action.payload },
    }),
    [confirmCode.pending.type]: (state) => ({
      ...state,
      checking: { ...state.checking, auth: true },
      checked: { ...state.checked, auth: null },
      error: { ...state.error, auth: null },
    }),
    [confirmCode.fulfilled.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, auth: false },
      checked: { ...state.checked, auth: action.payload },
    }),
    [confirmCode.rejected.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, auth: false },
      checked: { ...state.checked, auth: false },
      error: { ...state.error, auth: action.payload },
    }),
    [sendEmail.pending.type]: (state) => ({
      ...state,
      checking: { ...state.checking, send: true },
      checked: { ...state.checked, send: false },
      error: { ...state.error, send: null },
    }),
    [sendEmail.fulfilled.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, send: false },
      checked: { ...state.checked, send: action.payload },
    }),
    [sendEmail.rejected.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, send: false },
      checked: { ...state.checked, send: null },
      error: { ...state.error, send: action.payload },
    }),
  },
});

const { reducer: SignupReducer, actions } = signupSlice;
export const { uncheck, init, setValue, setSignUpMode, signUpInit } = actions;

export default SignupReducer;
